import { normalizeDrill, type Drill } from "../domain/drill";
import { loadDrills, replaceDrills } from "./drill-storage";

const WORKSPACE_KEY = "puckly-coach-workspace-key-v1";
const API = "/api/coach-drills";

export type CloudSyncResult = {
  drills: Drill[];
  synced: boolean;
};

export function getWorkspaceKey(
  storage: Pick<Storage, "getItem" | "setItem"> = localStorage,
) {
  const saved = storage.getItem(WORKSPACE_KEY);
  if (saved && /^[a-f0-9]{64}$/.test(saved)) return saved;
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const generated = [...bytes]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  storage.setItem(WORKSPACE_KEY, generated);
  return generated;
}

export function setWorkspaceKey(
  key: string,
  storage: Pick<Storage, "setItem"> = localStorage,
) {
  const normalized = key.trim().toLowerCase();
  if (!/^[a-f0-9]{64}$/.test(normalized)) return false;
  storage.setItem(WORKSPACE_KEY, normalized);
  return true;
}

const headers = () => ({
  "Content-Type": "application/json",
  "X-Coach-Workspace-Key": getWorkspaceKey(),
});

export async function syncDrills(): Promise<CloudSyncResult> {
  const local = loadDrills();
  try {
    const response = await fetch(API, { headers: headers() });
    if (!response.ok) throw new Error(`Cloud load returned ${response.status}`);
    const body = (await response.json()) as { drills?: Drill[] };
    const remote = Array.isArray(body.drills)
      ? body.drills.map(normalizeDrill)
      : [];
    const merged = new Map<string, Drill>();
    for (const drill of [...remote, ...local]) {
      const existing = merged.get(drill.id);
      if (!existing || drill.updatedAt > existing.updatedAt)
        merged.set(drill.id, drill);
    }
    const drills = replaceDrills([...merged.values()]);
    if (drills.length)
      await fetch(API, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ drills }),
      });
    return { drills, synced: true };
  } catch {
    return { drills: local, synced: false };
  }
}

export async function saveCloudDrill(drill: Drill) {
  try {
    const response = await fetch(API, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ drills: [drill] }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function deleteCloudDrill(id: string) {
  try {
    const response = await fetch(`${API}?id=${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: headers(),
    });
    return response.ok;
  } catch {
    return false;
  }
}
